import React, { createContext, useContext, useState, useEffect } from "react";
import { msalInstance } from "../msalConfig";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState(null);
  const [msalInitialized, setMsalInitialized] = useState(false);
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        console.log("Initializing MSAL...");

        if (msalInstance.initialize) {
          await msalInstance.initialize();
        }

        setMsalInitialized(true);
        console.log("MSAL initialization state set to true.");

        const response = await msalInstance.handleRedirectPromise();
        if (response) {
          setIsAuthenticated(true);
          setUser(response.account);
          setRoles(response.account.idTokenClaims?.roles ?? [])
        } else {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            setIsAuthenticated(true);
            console.log(accounts);
            setUser(accounts[0]);
          setRoles(accounts[0].idTokenClaims?.roles ?? [])
          } else {
            setIsAuthenticated(false);
          }
        }
      } catch (error) {
        console.error("MSAL initialization or redirect handling failed", error);
        setIsAuthenticated(false);
      }
    };

    initializeMsal();
  }, []);

  useEffect(() => {
    console.log(
      "Auth state changed. isAuthenticated:",
      isAuthenticated,
      "User:",
      user
    );
  }, [isAuthenticated, user]);

  const login = () => {
    if (!msalInitialized) {
      console.error("MSAL is not initialized.");
      return;
    } 
    msalInstance.loginRedirect({ scopes: ["user.read"] });
  };

  const acquireToken = async (scopes) => {
    if (!user) {
      throw new Error("User is not authenticated");
    }
    
    // Token expiry check: Assuming user.idToken and user.idTokenExpiry exist
    const currentTime = Date.now() / 1000; // Current time in seconds
    if (user.idTokenExpiry && currentTime > user.idTokenExpiry) {
      // Token is expired, acquire a new token
      try {
        const response = await msalInstance.acquireTokenSilent({
          scopes: scopes,
          account: user
        });
        // Update user state with new token and expiry
        setUser({
          ...user,
          idToken: response.idToken,
          idTokenExpiry: response.expiresOn.getTime() / 1000 // Store expiry in seconds
        });
        return response.idToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          // Handle interactive authentication if needed
          console.error("Silent token acquisition failed, user interaction required", error);
          // Redirect to login or show a login prompt
        } else {
          console.error("Token acquisition failed", error);
        }
        throw error;
      }
    } else {
      // Return the existing token
      return user.idToken;
    }
  };

  const logout = () => {
    if (!msalInitialized) {
      console.error("MSAL is not initialized.");
      return;
    }
    msalInstance.logoutRedirect();
    setIsAuthenticated(false);
    setUser(null);
    setRoles([])
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, user, msalInitialized, login, logout, acquireToken }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
